import { daysTo, parseDate, pln } from 'app/common/format/vz-format';
import { Financing, Money, Policy, Vehicle } from './panel.models';

/**
 * Wartości wyliczane z list (Kontrakt API v1 — „Wartości wyliczane”), wspólne dla strony głównej,
 * list i stron szczegółów. Panel niczego nie przelicza (K3): sumuje kwoty z CRM, brutto tylko tam,
 * gdzie CRM je ma; daty i liczby rat liczy z pól umowy i polisy.
 */

const DAY_MS = 86_400_000;

/** Polisa dłuższa niż ~13 miesięcy (np. GAP na okres leasingu) — jej składka nie jest roczna (N16). */
export const MULTI_YEAR_DAYS = 400;

/** Polisa „kończy się” w ≤ 30 dni, umowa w < 90 dni (Kontrakt API v1). */
export const POLICY_SOON_DAYS = 30;
export const CONTRACT_SOON_DAYS = 90;

export const sum = (values: number[]): number => Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100;

/** Rosnąco po dacie; brak daty na końcu. */
export const byDate = (a: string | null, b: string | null): number =>
    (a ? parseDate(a).getTime() : Infinity) - (b ? parseDate(b).getTime() : Infinity);

// ------------------------------------------------------------------ pojazdy

export function vehicleName(vehicle: Vehicle | null | undefined): string {
    return vehicle ? [vehicle.mark, vehicle.model].filter(Boolean).join(' ') : '';
}

export type VehicleFilter = 'in_use' | 'purchasing' | 'all' | 'sold';

export const VEHICLE_FILTERS: VehicleFilter[] = ['in_use', 'purchasing', 'all', 'sold'];

/** „Wszystkie” = w użytkowaniu + w zakupie, bez sprzedanych (Issue 20). */
export function matchesVehicleFilter(vehicle: Vehicle, filter: VehicleFilter): boolean {
    if (filter === 'all') {
        return vehicle.usage !== 'sold';
    }

    return vehicle.usage === filter;
}

/** Porównanie bez wielkości liter i polskich znaków („lodz” znajduje „Łódź”). */
export function fold(value: string): string {
    return value.toLocaleLowerCase('pl').replace(/ł/g, 'l').normalize('NFD').replace(/\p{M}/gu, '');
}

/** Szukanie w listach i wyszukiwarce: fragment tekstu, także bez spacji („po5hn72” znajduje „PO 5HN72”). */
export function matchesText(query: string, ...values: (string | number | null | undefined)[]): boolean {
    const q = fold(query.trim());

    if (!q) {
        return true;
    }

    const haystack = fold(values.filter((v) => v !== null && v !== undefined && v !== '').join(' '));

    return haystack.includes(q) || haystack.replace(/\s+/g, '').includes(q.replace(/\s+/g, ''));
}

export function vehicleMatchesQuery(vehicle: Vehicle, query: string): boolean {
    return matchesText(query, vehicle.mark, vehicle.model, vehicle.version, vehicle.registrationNumber, vehicle.vin);
}

export interface VehiclesTile {
    value: number | null;
    count: number;
    marginCount: number;
    /** Suma netto pojazdów z rozbiciem (bez VAT marży) — podpis kafla, gdy nie ma aut w marży. */
    netSum: number | null;
}

/** Wartość pojazdów w użytkowaniu i w zakupie — sprzedane się nie liczą (FM 13.10). */
export function vehicleValue(vehicles: Vehicle[], includeSold = false): VehiclesTile {
    const owned = includeSold ? vehicles : vehicles.filter((v) => v.usage !== 'sold');
    const priced = owned.filter((v) => v.price?.gross != null);
    const withNet = owned.filter((v) => v.price?.vatRate !== 'margin' && v.price?.net != null);

    return {
        value: priced.length ? sum(priced.map((v) => v.price.gross)) : null,
        count: owned.length,
        marginCount: owned.filter((v) => v.price?.vatRate === 'margin').length,
        netSum: withNet.length ? sum(withNet.map((v) => v.price.net)) : null,
    };
}

// ------------------------------------------------------------------ polisy

export function isActivePolicy(policy: Policy, now: Date): boolean {
    if (policy.closedAt || !policy.validFrom || !policy.validTo) {
        return false;
    }

    return daysTo(policy.validFrom, now) <= 0 && daysTo(policy.validTo, now) >= 0;
}

/** Kropka przy „Polisy” i baner: aktywna polisa kończy się w ≤ 30 dni. */
export function isEndingSoon(policy: Policy, now: Date): boolean {
    return isActivePolicy(policy, now) && daysTo(policy.validTo, now) <= POLICY_SOON_DAYS;
}

export function periodDays(policy: Policy): number {
    return (parseDate(policy.validTo).getTime() - parseDate(policy.validFrom).getTime()) / DAY_MS;
}

export function isMultiYear(policy: Policy): boolean {
    return !!policy.validFrom && !!policy.validTo && periodDays(policy) > MULTI_YEAR_DAYS;
}

/** W przygotowaniu · aktywna · kończy się (≤ 30 dni) · zakończona (także zamknięta ręcznie) · jeszcze się nie zaczęła. */
export type PolicyState = 'preparing' | 'active' | 'soon' | 'ended' | 'future';

export function policyState(policy: Policy, now: Date): PolicyState {
    if (policy.status === 'preparing') {
        return 'preparing';
    }

    if (policy.closedAt || (policy.validTo && daysTo(policy.validTo, now) < 0)) {
        return 'ended';
    }

    if (policy.validFrom && daysTo(policy.validFrom, now) > 0) {
        return 'future';
    }

    return policy.validTo && daysTo(policy.validTo, now) <= POLICY_SOON_DAYS ? 'soon' : 'active';
}

export type PolicyFilter = 'active' | 'ended' | 'all';

export const POLICY_FILTERS: PolicyFilter[] = ['active', 'ended', 'all'];

export function matchesPolicyFilter(policy: Policy, filter: PolicyFilter, now: Date): boolean {
    if (filter === 'all') {
        return true;
    }

    return (policyState(policy, now) === 'ended') === (filter === 'ended');
}

/** Zakres do wyświetlenia: kategorie ze słownika ryzyk („OC + AC”), bez nich typ („Polisa”, „GAP”). */
export function policyScope(policy: Policy): string {
    return policy.scopeLabel ?? policy.type;
}

export function policyTitle(policy: Policy): string {
    return [policy.insurer, policyScope(policy)].filter(Boolean).join(' · ');
}

export function policyMatchesQuery(policy: Policy, vehicle: Vehicle | null, query: string): boolean {
    return matchesText(query, policy.insurer, policyScope(policy), policy.number, vehicle?.mark, vehicle?.model, vehicle?.registrationNumber);
}

/** Ułamek okresu ochrony za klientem (0–1); null bez dat. */
export function periodProgress(policy: Policy, now: Date): number | null {
    if (!policy.validFrom || !policy.validTo) {
        return null;
    }

    const from = parseDate(policy.validFrom).getTime();
    const to = parseDate(policy.validTo).getTime();

    return to > from ? Math.min(1, Math.max(0, (now.getTime() - from) / (to - from))) : null;
}

/** Polisy pojazdu od najbliższego końca; aktywne (i przyszłe) osobno od zakończonych. */
export function vehiclePolicies(vehicle: Vehicle, policies: Policy[], now: Date): { current: Policy[]; ended: Policy[] } {
    const own = policies.filter((p) => p.vehicleId === vehicle.id).sort((a, b) => byDate(a.validTo, b.validTo));

    return {
        current: own.filter((p) => policyState(p, now) !== 'ended'),
        ended: own.filter((p) => policyState(p, now) === 'ended').reverse(),
    };
}

export interface PremiumsTile {
    /** Suma składek aktywnych polis rocznych; null — żadna nie ma składki w CRM. */
    value: number | null;
    /** Aktywne polisy roczne — tych dotyczy suma. */
    annualCount: number;
    /** Aktywne polisy wieloletnie (np. GAP na okres leasingu) — poza sumą roczną (N16). */
    multiYearCount: number;
}

export function premiums(policies: Policy[], now: Date): PremiumsTile {
    const active = policies.filter((p) => isActivePolicy(p, now));
    const annual = active.filter((p) => periodDays(p) <= MULTI_YEAR_DAYS);
    const priced = annual.filter((p) => p.premium !== null);

    return {
        value: priced.length ? sum(priced.map((p) => p.premium)) : null,
        annualCount: annual.length,
        multiYearCount: active.length - annual.length,
    };
}

// ------------------------------------------------------------------ finansowanie

/** Kwota główna: brutto, a gdy CRM ma tylko netto — netto (D4, bez przeliczania). */
export function mainAmount(money: Money | null | undefined): { value: number; basis: 'gross' | 'net' } | null {
    if (money?.gross != null) {
        return { value: money.gross, basis: 'gross' };
    }

    return money?.net != null ? { value: money.net, basis: 'net' } : null;
}

export type ContractState = 'preparing' | 'active' | 'ending' | 'ended';

export function contractState(financing: Financing, now: Date): ContractState {
    if (financing.status === 'preparing') {
        return 'preparing';
    }

    if (financing.status === 'ended' || (financing.contractEndAt && daysTo(financing.contractEndAt, now) < 0)) {
        return 'ended';
    }

    return financing.contractEndAt && daysTo(financing.contractEndAt, now) < CONTRACT_SOON_DAYS ? 'ending' : 'active';
}

export type FinancingFilter = 'active' | 'ended' | 'all';

/** „Czynne” = wszystko poza zakończonymi (z umowami w przygotowaniu). */
export function matchesFinancingFilter(financing: Financing, filter: FinancingFilter, now: Date): boolean {
    if (filter === 'all') {
        return true;
    }

    return (contractState(financing, now) === 'ended') === (filter === 'ended');
}

export interface ContractProgress {
    paid: number;
    total: number;
    left: number;
    percent: number;
}

/**
 * Zapłacone raty = pełne miesiące od zawarcia umowy, najwyżej liczba rat — „wyliczone z okresu umowy”,
 * bo CRM nie prowadzi harmonogramu spłat (panel go nie buduje).
 */
export function contractProgress(financing: Financing, now: Date): ContractProgress | null {
    if (financing.status === 'preparing' || !financing.contractStartAt || !financing.periodMonths) {
        return null;
    }

    const start = parseDate(financing.contractStartAt);
    let months = (now.getFullYear() - start.getFullYear()) * 12 + now.getMonth() - start.getMonth();

    if (now.getDate() < start.getDate()) {
        months--;
    }

    const total = financing.periodMonths;
    const paid = Math.min(total, Math.max(0, months));

    return { paid, total, left: total - paid, percent: Math.round((paid / total) * 100) };
}

export interface InstallmentsTile {
    /** Suma rat brutto czynnych umów; netto, gdy CRM nie ma brutto (D4). null — brak umów z ratą. */
    value: number | null;
    basis: 'gross' | 'net';
    netSum: number;
    count: number;
    /** „+ X po podpisaniu” — umowy w przygotowaniu z ratą po decyzji leasingodawcy. */
    afterSigning: number | null;
}

const withRate = (f: Financing): boolean => f.rate !== null && (f.rate.gross !== null || f.rate.net !== null);

export function installments(financing: Financing[]): InstallmentsTile {
    const active = financing.filter((f) => f.status === 'active' && withRate(f));
    const allGross = active.length > 0 && active.every((f) => f.rate.gross !== null);
    const amount = (f: Financing) => (allGross ? f.rate.gross : f.rate.net) ?? 0;
    const preparing = financing.filter((f) => f.status === 'preparing' && withRate(f));

    return {
        value: active.length ? sum(active.map(amount)) : null,
        basis: allGross ? 'gross' : 'net',
        netSum: sum(active.map((f) => f.rate.net ?? 0)),
        count: active.length,
        afterSigning: preparing.length ? sum(preparing.map(amount)) : null,
    };
}

export interface RemainingTile {
    /** Raty pozostałe do końca umów × rata, bez wykupu; null — żadna czynna umowa nie ma raty i okresu. */
    value: number | null;
    basis: 'gross' | 'net';
}

export function remaining(financing: Financing[], now: Date): RemainingTile {
    const rows = financing
        .filter((f) => f.status === 'active' && withRate(f))
        .map((f) => ({ f, progress: contractProgress(f, now) }))
        .filter((row) => row.progress !== null);
    const allGross = rows.length > 0 && rows.every((row) => row.f.rate.gross !== null);

    return {
        value: rows.length
            ? sum(rows.map((row) => row.progress.left * ((allGross ? row.f.rate.gross : row.f.rate.net) ?? 0)))
            : null,
        basis: allGross ? 'gross' : 'net',
    };
}

/** Najbliższy koniec czynnej umowy. */
export function nearestContractEnd(financing: Financing[], now: Date): Financing | null {
    return (
        financing
            .filter((f) => f.contractEndAt && contractState(f, now) !== 'ended' && contractState(f, now) !== 'preparing')
            .sort((a, b) => byDate(a.contractEndAt, b.contractEndAt))[0] ?? null
    );
}

/**
 * „Produkt dodatkowy” z CRM to jedno pole tekstowe — sama liczba (np. „2000”, „1 500,50”) to kwota w zł, inaczej
 * tekst bez zmian, np. nazwa produktu z kwotą (Issue 46 C8).
 */
export function additionalProductText(value: string): string {
    const normalized = value.replace(/\s/g, '').replace(',', '.');

    return /^\d+(\.\d{1,2})?$/.test(normalized) ? pln(Number(normalized)) : value;
}

export type RiskGroup = 'oc' | 'ac' | 'extras';

/** Grupy ryzyk z układu 22.09 (N5): OC, AC i kradzież, dodatki — zakres w etapie i na stronie polisy (Issue 46 C1). */
export function riskGroupOf(category: string): RiskGroup {
    if (['oc', 'bls', 'oc_discount'].includes(category)) {
        return 'oc';
    }

    return ['ac', 'aso', 'partner_network', 'estimate', 'ac_discount'].includes(category) ? 'ac' : 'extras';
}
